<template>
    <UPopover v-if="managers.length > 0" v-model:open="open" :disabled="!canOpenManagerMenu">
        <UButton
            color="neutral"
            variant="ghost"
            :trailing-icon="canOpenManagerMenu ? 'i-lucide-chevron-down' : undefined"
            block
            :ui="{ base: 'justify-start gap-3', trailingIcon: 'text-primary size-4 stroke-[3]' }"
        >
            <span class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/12">
                <UIcon name="i-lucide-building-2" class="size-5 text-primary" />
            </span>
            <span class="flex min-w-0 flex-col text-left">
                <span class="text-sm font-semibold tracking-wide text-primary uppercase">
                    {{ $t('manager.label') }}
                </span>
                <span class="line-clamp-2 text-sm leading-4 font-semibold">
                    {{ displayManager }}
                </span>
            </span>
        </UButton>

        <template #content>
            <div class="w-80 max-w-[calc(100vw-2rem)] p-2">
                <UInput
                    v-if="showSearch"
                    v-model="searchManagersValue"
                    class="mb-2 w-full"
                    size="sm"
                    icon="i-lucide-search"
                    :placeholder="$t('manager.search')"
                    @update:model-value="searchManagers"
                />

                <ul class="flex max-h-72 flex-col gap-0.5 overflow-auto">
                    <li v-for="manager in managersToDisplay" :key="`${manager.manager}_${manager.code_payer}`">
                        <button
                            type="button"
                            class="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-elevated"
                            :class="{ 'bg-primary/10 font-semibold': isSelected(manager) }"
                            @click="onSelectManager(manager)"
                        >
                            <span class="min-w-0 flex-1">{{ manager.full_title_with_code_payer }}</span>
                            <UIcon v-if="isSelected(manager)" name="i-lucide-check" class="size-4 shrink-0 text-primary" />
                        </button>
                    </li>
                    <li v-if="managersToDisplay.length === 0" class="px-3 py-2 text-sm text-muted">
                        {{ $t('manager.no_results') }}
                    </li>
                </ul>
            </div>
        </template>
    </UPopover>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref, type Ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'

    import { parseError, parseErrorAuth } from '@/libs/error'
    import { PrintError, PrintSuccess } from '@/libs/toast'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import type { UserManager } from '@/types/Manager.ts'
    import type { UserData } from '@/types/User.ts'

    // config
    const SEARCH_THRESHOLD = 7

    // data
    const { t } = useI18n()
    const appStore = useAppStore()
    const fetchStore = useFetchStore()

    const managers: Ref<UserManager[]> = ref([])
    const selectedManager: Ref<UserManager | undefined> = ref(undefined)

    const filteredManagers: Ref<UserManager[]> = ref([])
    const searchManagersValue: Ref<string> = ref('')
    let searchManagersTimeout: ReturnType<typeof setTimeout> | undefined

    const open: Ref<boolean> = ref(false)

    // computed
    const loggedIn = computed(() => {
        return appStore.getUserLoggedIn()
    })

    const userData = computed(() => {
        return appStore.getUserData()
    })

    const displayManager = computed(() => {
        if (!selectedManager.value) return ''

        return selectedManager.value.full_title_with_code_payer
    })

    const managersToDisplay = computed(() => {
        const base = searchManagersValue.value ? filteredManagers.value : managers.value
        const sel = selectedManager.value

        let ordered = base
        if (sel) {
            const idx = base.findIndex(m => m.manager === sel.manager && m.code_payer === sel.code_payer)
            if (idx > 0) {
                ordered = [...base]
                const [item] = ordered.splice(idx, 1)
                if (item) ordered.unshift(item)
            }
        }

        return searchManagersValue.value ? ordered : ordered.slice(0, 10)
    })

    const canOpenManagerMenu = computed(() => {
        return managers.value.length > 1
    })

    const showSearch = computed(() => {
        return managers.value.length > SEARCH_THRESHOLD
    })

    // methods
    const isSelected = (manager: UserManager) => {
        return !!selectedManager.value
            && selectedManager.value.manager === manager.manager
            && selectedManager.value.code_payer === manager.code_payer
    }

    const checkDuplicateManagers = () => {
        const managerCounts: Record<string, number> = {}

        managers.value.forEach((manager) => {
            managerCounts[manager.manager] = (managerCounts[manager.manager] || 0) + 1
        })

        if (Object.keys(managerCounts).length === 0) return

        for (const manager of managers.value) {
            if ((managerCounts[manager.manager] ?? 0) > 1) manager.full_title_with_code_payer = `${manager.full_title} - ${manager.code_payer}`
            else manager.full_title_with_code_payer = manager.full_title
        }

        managers.value = managers.value.sort((a, b) => a.full_title_with_code_payer.toLowerCase().localeCompare(b.full_title_with_code_payer.toLowerCase()))

        filteredManagers.value = managers.value
    }

    const loadData = async() => {
        if (!loggedIn.value) return
        appStore.startLoading()
        const { response, data, error } = await fetchStore.fetch('/api/auth/v1/user_managers').get().json<UserManager[]>()
        if (response.value?.status === 200 && data.value) {
            managers.value = data.value

            checkDuplicateManagers()

            const index = managers.value.findIndex(x => x.manager === userData.value.selected_manager && x.code_payer === userData.value.code_payer)
            if (index > -1) selectedManager.value = managers.value[index]
            else if (managers.value.length > 0 && managers.value[0]) selectedManager.value = managers.value[0]

            appStore.setSelectedManager(selectedManager.value)
        } else {
            PrintError(parseError(error.value ?? data.value, t('errors.load_data')))
        }
        appStore.stopLoading()
    }

    const onSelectManager = async(manager: UserManager) => {
        open.value = false
        appStore.startLoading()

        const { response, data, error } = await fetchStore
            .fetch(`/api/auth/v1/switch_manager/${manager.manager}/${manager.code_payer}`)
            .post()
            .json<{ user_data: UserData, token_string: string }>()
        if (response.value?.status === 200 && data.value) {
            appStore.setLogin(data.value.user_data, data.value.token_string)
            PrintSuccess(t('manager.switch_success'))
            appStore.setSelectedUnit(undefined)
            window.location.reload()
        } else {
            PrintError(parseErrorAuth(error.value ?? data.value, t('manager.switch_error')))
        }
        appStore.stopLoading()
    }

    const searchManagers = () => {
        if (searchManagersValue.value.length === 0) {
            filteredManagers.value = managers.value
            return
        }
        if (searchManagersTimeout) clearTimeout(searchManagersTimeout)

        searchManagersTimeout = setTimeout(() => {
            filteredManagers.value = managers.value.filter(x => x.full_title_with_code_payer.toLowerCase().indexOf(searchManagersValue.value.toLowerCase()) !== -1)
        }, 200)
    }

    // watcher
    watch(open, (isOpen) => {
        if (isOpen) searchManagersValue.value = ''
    })

    // hook
    onMounted(async() => {
        await loadData()
    })
</script>
